import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Autocomplete,
  TextField,
  Grid,
  IconButton,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useStyles } from "../../../../Theme/Style";
import { Form, Formik, FieldArray } from "formik";
import { Delete, PictureAsPdf, AddCircleOutline } from "@mui/icons-material";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { useNotification } from "../../../../Contexts/GlobalNotificationContext";
import {
  useSubmitEmbroideryMutation,
  useUploadEmbroideryImageMutation,
  useGetEmbroideryQuery,
} from "../../../../services/api/production-order";
import {
  useGetEmbroideryLibraryListQuery,
  useGetOrganizationsAllListQuery,
} from "../../../../services/api/embroidery-library";

// Schema for a single embroidery item
const embroideryItemSchema = Yup.object({
  design_name: Yup.string().required("Design Name is required"),
  ribbon: Yup.string().nullable(),
  logo: Yup.mixed().nullable(),
  width: Yup.number().typeError("Width must be a number").required("Width is required"),
  height: Yup.number().typeError("Height must be a number").required("Height is required"),
  embroideryDiskName: Yup.string().required("Embroidery Disk Number is required"),
  remark: Yup.string().nullable(),
});

// Full form schema – array of embroideries
const validationSchema = Yup.object({
  embroideries: Yup.array().of(embroideryItemSchema).min(1),
});

// Map API record → form item
const buildEmbroideryFromSrc = (src = {}) => ({
  design_name: src.design_name || "",
  ribbon: src.ribbon || "",
  logo: src.logo || null,              // path in storage
  logo_url: src.logo_url || null,      // full URL for preview/download
  width: src.width || "",
  height: src.height || "",
  embroideryDiskName: src.disk_name || src.embroideryDiskName || "",
  remark: src.remark || "",
  // UI-only
  library: null,
});

const EmbroideryComponent = ({ prodId }) => {
  const classes = useStyles();
  const { addNotification } = useNotification();

  // Orgs & libraries
  const { data: organizations = [] } = useGetOrganizationsAllListQuery();
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const { data: libraries = [] } = useGetEmbroideryLibraryListQuery(selectedOrgId);

  // Mutations/queries
  const [uploadEmbroideryImages] = useUploadEmbroideryImageMutation();
  const [submitEmbroidery] = useSubmitEmbroideryMutation();

  // Treat embroideryData as an ARRAY
  const { data: embroideryData = [] } = useGetEmbroideryQuery({ id: prodId });

  console.log("Embroidery data:", embroideryData);

  // Form initial values – ARRAY of embroideries
  const [initialValues, setInitialValues] = useState({
    embroideries: [buildEmbroideryFromSrc()],
  });

  // Preselect org from first record (if any)
  useEffect(() => {
    if (
      !selectedOrgId &&
      Array.isArray(embroideryData) &&
      embroideryData.length > 0 &&
      embroideryData[0].organization_id
    ) {
      setSelectedOrgId(embroideryData[0].organization_id);
    }
  }, [embroideryData, selectedOrgId]);

  // Initialize from existing embroidery records
  useEffect(() => {
    if (Array.isArray(embroideryData) && embroideryData.length > 0) {
      setInitialValues({
        embroideries: embroideryData.map((item) => buildEmbroideryFromSrc(item)),
      });
    }
  }, [embroideryData]);

  const handleFileUpload = async (file, isPdf = false) => {
    const formData = new FormData();
    formData.append(isPdf ? "file" : "image", file);
    return uploadEmbroideryImages({ data: formData }).unwrap(); // { path, url }
  };

  // 🔹 special handler for logo PDF so we can set both path + url
  const handleLogoFileChange = async (e, index, setFieldValue) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await handleFileUpload(file, true); // PDF
      // API expected to return { path, url }
      setFieldValue(`embroideries[${index}].logo`, res.path);
      if (res.url) {
        setFieldValue(`embroideries[${index}].logo_url`, res.url);
      }
    } catch (err) {
      addNotification({ message: "Upload error", type: "error" });
    }
  };

  const onSubmitData = async (values, { setSubmitting }) => {
    try {
      // Strip UI-only `library` and `logo_url` fields before sending
      const embroideriesPayload = values.embroideries.map((emb) => ({
        design_name: emb.design_name,
        ribbon: emb.ribbon,
        logo: emb.logo, // server expects path
        width: emb.width,
        height: emb.height,
        disk_name: emb.embroideryDiskName,
        production_order_id: prodId,
        remark: emb.remark,
      }));

      const data = {
        production_order_id: prodId,
        embroideries: embroideriesPayload,
      };

      await submitEmbroidery({ data }).unwrap();
      addNotification({ message: "Submitted", type: "success" });
    } catch (e) {
      addNotification({ message: "Error", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
        className={classes.accordionSummary}
      >
        <Grid container className={classes.accordionSummaryTextContainer}>
          <Grid item xs={6}>Embroidery</Grid>
          <Grid item xs={6}>刺绣</Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails className={classes.accordionDetails}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={onSubmitData}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              {/* Organization selector (global) */}
              <Autocomplete
                options={organizations}
                getOptionLabel={(o) => o.name_with_id}
                value={organizations.find((o) => o.id === selectedOrgId) || null}
                onChange={(_, v) => setSelectedOrgId(v?.id || null)}
                renderInput={(params) => <TextField {...params} label="Organization" />}
                sx={{ mb: 2 }}
              />

              <FieldArray name="embroideries">
                {({ push, remove }) => (
                  <>
                    {values.embroideries.map((emb, index) => {
                      const embErrors =
                        (errors.embroideries && errors.embroideries[index]) || {};
                      const embTouched =
                        (touched.embroideries && touched.embroideries[index]) || {};

                      const logoLink = emb.logo_url || emb.logo || null;
                      const logoFileName = emb.logo ? emb.logo.split("/").pop() : null;

                      return (
                        <Box
                          key={index}
                          sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <Typography variant="subtitle1">
                              Logo #{index + 1}
                            </Typography>
                            {values.embroideries.length > 1 && (
                              <IconButton size="small" onClick={() => remove(index)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>

                          {/* Library selector for this embroidery */}
                          <Autocomplete
                            options={libraries}
                            getOptionLabel={(lib) => lib.disk_name || ""}
                            value={emb.library || null}
                            onChange={(_, lib) => {
                              setFieldValue(`embroideries[${index}].library`, lib);

                              if (lib) {
                                setFieldValue(
                                  `embroideries[${index}].design_name`,
                                  lib.design_name || ""
                                );
                                setFieldValue(
                                  `embroideries[${index}].embroideryDiskName`,
                                  lib.disk_name || ""
                                );
                                setFieldValue(
                                  `embroideries[${index}].width`,
                                  lib.width || ""
                                );
                                setFieldValue(
                                  `embroideries[${index}].height`,
                                  lib.height || ""
                                );
                                setFieldValue(
                                  `embroideries[${index}].ribbon`,
                                  lib.ribbon || ""
                                );
                                if (lib.logo) {
                                  setFieldValue(
                                    `embroideries[${index}].logo`,
                                    lib.logo
                                  );
                                }
                                if (lib.logo_url) {
                                  setFieldValue(
                                    `embroideries[${index}].logo_url`,
                                    lib.logo_url
                                  );
                                }
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Library"
                                placeholder={
                                  selectedOrgId
                                    ? "Choose from organization’s libraries"
                                    : "Select organization first"
                                }
                              />
                            )}
                            disabled={!selectedOrgId}
                            sx={{ mb: 2 }}
                          />

                          <Grid container spacing={2}>
                            {/* Core fields */}
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                label="Design Name"
                                value={emb.design_name}
                                onChange={(e) =>
                                  setFieldValue(
                                    `embroideries[${index}].design_name`,
                                    e.target.value
                                  )
                                }
                                error={
                                  embTouched.design_name &&
                                  Boolean(embErrors.design_name)
                                }
                                helperText={
                                  embTouched.design_name && embErrors.design_name
                                }
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                label="Embroidery Disk Number"
                                value={emb.embroideryDiskName}
                                onChange={(e) =>
                                  setFieldValue(
                                    `embroideries[${index}].embroideryDiskName`,
                                    e.target.value
                                  )
                                }
                                error={
                                  embTouched.embroideryDiskName &&
                                  Boolean(embErrors.embroideryDiskName)
                                }
                                helperText={
                                  embTouched.embroideryDiskName &&
                                  embErrors.embroideryDiskName
                                }
                              />
                            </Grid>

                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                label="Width"
                                value={emb.width}
                                onChange={(e) =>
                                  setFieldValue(
                                    `embroideries[${index}].width`,
                                    e.target.value
                                  )
                                }
                                error={embTouched.width && Boolean(embErrors.width)}
                                helperText={embTouched.width && embErrors.width}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                label="Height"
                                value={emb.height}
                                onChange={(e) =>
                                  setFieldValue(
                                    `embroideries[${index}].height`,
                                    e.target.value
                                  )
                                }
                                error={embTouched.height && Boolean(embErrors.height)}
                                helperText={embTouched.height && embErrors.height}
                              />
                            </Grid>

                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                label="Ribbon"
                                value={emb.ribbon}
                                onChange={(e) =>
                                  setFieldValue(
                                    `embroideries[${index}].ribbon`,
                                    e.target.value
                                  )
                                }
                              />
                            </Grid>

                            <Divider flexItem sx={{ my: 1, width: "100%" }} />

                            {/* PDF upload + preview/download */}
                            <Grid item xs={12}>
                              <input
                                accept="application/pdf"
                                style={{ display: "none" }}
                                id={`logo-pdf-${index}`}
                                type="file"
                                onChange={(e) =>
                                  handleLogoFileChange(e, index, setFieldValue)
                                }
                              />
                              <label htmlFor={`logo-pdf-${index}`}>
                                <Button
                                  startIcon={<PictureAsPdf />}
                                  component="span"
                                >
                                  {logoFileName ? logoFileName : "Upload PDF Logo"}
                                </Button>
                              </label>

                              {logoLink && (
                                <Box mt={1} sx={{ display: "flex", gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="text"
                                    href={logoLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Preview
                                  </Button>
                                </Box>
                              )}
                            </Grid>

                            

                            <Divider flexItem sx={{ my: 1, width: "100%" }} />

                            {/* Remark */}
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Remark"
                                value={emb.remark}
                                onChange={(e) =>
                                  setFieldValue(
                                    `embroideries[${index}].remark`,
                                    e.target.value
                                  )
                                }
                                multiline
                                rows={3}
                              />
                            </Grid>
                          </Grid>

                          {/* Add Logo button (duplicates this block) ABOVE Remark */}
                          {index === values.embroideries.length - 1 && (
                            
                              <Grid item xs={12 } sx={{ mt: 5 }}>
                                <Divider flexItem sx={{ my: 3, width: "100%" }} />
                                <Button
                                  variant="outlined"
                                  startIcon={<AddCircleOutline />}
                                  onClick={() => {
                                    const clone = {
                                      ...emb,
                                      library: null,
                                    };
                                    push(clone);
                                  }}
                                >
                                  Add Logo
                                </Button>
                              </Grid>
                            )}
                        </Box>
                      );
                    })}
                  </>
                )}
              </FieldArray>

              <Box mt={3}>
                <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
                  Save
                </LoadingButton>
              </Box>
            </Form>
          )}
        </Formik>
      </AccordionDetails>
    </Accordion>
  );
};

export default EmbroideryComponent;
